import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, Descriptions, Tag, Typography, Button, Spin, Space, Modal, Input, Table, message, Form } from 'antd'
import { ArrowLeftOutlined, DownloadOutlined, FilePdfOutlined, RollbackOutlined } from '@ant-design/icons'
import {
  createRefundRequest,
  downloadOrderInvoice,
  getOrder,
  getOrderRefundRequests,
  getOrderRefunds,
  issueOrderInvoice,
  type IssueOrderInvoiceRequest,
} from '../../services/order'
import { formatDate, formatTimestamp } from '../../utils/format'
import { formatAmount, formatDisplayExchangeRate } from '../../utils/orderDisplay'
import { orderDisplayStatus, orderStatusColors, orderStatusLabels, shouldShowOrderFees } from '../../utils/orderDisplayStatus'
import { billingReasonText } from '../subscriptions/subscriptionLabels'
import { useMerchantStore } from '../../stores/merchantStore'
import type { Order, Refund, RefundRequest } from '../../types'

const { Title } = Typography
const { TextArea } = Input

const paymentMethodLabels: Record<string, string> = {
  CREDIT_CARD: '信用卡',
  APPLE_PAY: 'Apple Pay',
  GOOGLE_PAY: 'Google Pay',
  PAYPAL: 'PayPal',
  WECHAT_PAY: '微信支付',
  ALIPAY: '支付寶',
  ALIPAY_HK: 'AlipayHK',
  BOOST: 'Boost',
  TOUCH_N_GO_EWALLET: "Touch'n Go",
  LINE_PAY: 'LINE Pay',
  KREDIVO: 'Kredivo',
  GCASH: 'GCash',
  JKOPAY: 'JKOPay',
  MIR: 'Mir',
  SBP: 'SBP',
  T_PAY: 'T-Pay',
  SBER_PAY: 'Sber Pay',
  YOO_MONEY: 'YooMoney',
  PAYNOW: 'PayNow',
  QRIS: 'QRIS',
  OVO: 'OVO SNAP',
  DANA: 'DANA',
  SHOPEE_PAY: 'ShopeePay',
  VIRTUAL_ACCOUNT: 'Virtual Account / 銀行轉賬',
  CRYPTO: '加密貨幣',
}

const refundRequestStatusMeta: Record<string, { color: string; label: string }> = {
  PENDING: { color: 'gold', label: '待審核' },
  REVIEWING: { color: 'blue', label: '審核中' },
  APPROVED: { color: 'green', label: '已批准' },
  REJECTED: { color: 'red', label: '已拒絕' },
}

function formatOptionalAmount(value?: string | null, currency?: string) {
  return value == null ? '-' : `${formatAmount(value)} ${currency || ''}`.trim()
}

function formatOptionalUsdAmount(value?: string | null) {
  return value == null ? '-' : `${formatAmount(value)} USD`
}

function formatOptionalRate(value?: string | null) {
  if (value == null) return '-'
  const numeric = Number(value)
  return Number.isFinite(numeric) ? `${(numeric * 100).toFixed(2)}%` : '-'
}

function formatRateAndPerTransactionFee(rate?: string | null, perTransactionFee?: string | null) {
  return `${formatOptionalRate(rate)} / ${formatOptionalUsdAmount(perTransactionFee)}`
}

export default function OrderDetail() {
  const { id } = useParams<{ id: string }>()
  const [order, setOrder] = useState<Order | null>(null)
  const [refundRequests, setRefundRequests] = useState<RefundRequest[]>([])
  const [refunds, setRefunds] = useState<Refund[]>([])
  const [loading, setLoading] = useState(true)
  const [refundModalOpen, setRefundModalOpen] = useState(false)
  const [refundAmount, setRefundAmount] = useState('')
  const [refundReason, setRefundReason] = useState('')
  const [submittingRefund, setSubmittingRefund] = useState(false)
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false)
  const [invoiceDownloading, setInvoiceDownloading] = useState(false)
  const [invoiceForm] = Form.useForm<IssueOrderInvoiceRequest>()
  const merchant = useMerchantStore((state) => state.merchant)
  const navigate = useNavigate()

  const loadRefundData = async (orderId: string) => {
    const [requestRes, refundRes] = await Promise.all([
      getOrderRefundRequests(orderId),
      getOrderRefunds(orderId),
    ])
    setRefundRequests(requestRes.data)
    setRefunds(refundRes.data)
  }

  useEffect(() => {
    if (id) {
      Promise.all([getOrder(id), loadRefundData(id)])
        .then(([res]) => setOrder(res.data))
        .finally(() => setLoading(false))
    }
  }, [id])

  const hasActiveRefundRequest = refundRequests.some((request) =>
    request.status === 'PENDING' || request.status === 'REVIEWING'
  )
  const canRequestRefund =
    merchant?.refundable === true &&
    (order?.status === 'PAID' || order?.status === 'SETTLED') &&
    !hasActiveRefundRequest
  const canManageInvoice =
    merchant?.invoiceEnabled === true &&
    order?.type === 'PAYMENT' &&
    order.paidAt != null
  const orderAmount = order ? (order.baseAmount ?? order.amount) : undefined
  const payableAmount = order ? (order.payableAmount ?? order.amount) : undefined

  const openRefundModal = () => {
    if (!payableAmount) return
    setRefundAmount(payableAmount)
    setRefundReason('')
    setRefundModalOpen(true)
  }

  const submitRefundRequest = async () => {
    if (!id) return
    if (!refundAmount || !refundReason.trim()) {
      message.warning('請填寫退款金額和原因')
      return
    }
    setSubmittingRefund(true)
    try {
      await createRefundRequest(id, { amount: refundAmount, reason: refundReason.trim() })
      message.success('退款申請已提交')
      setRefundModalOpen(false)
      await loadRefundData(id)
    } catch (e) {
      message.error(e instanceof Error ? e.message : '提交失敗')
    } finally {
      setSubmittingRefund(false)
    }
  }

  const saveInvoicePdf = (blob: Blob) => {
    if (!id) return
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `invoice-${id}.pdf`
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }

  const downloadExistingInvoice = async () => {
    if (!id) return
    setInvoiceDownloading(true)
    try {
      saveInvoicePdf(await downloadOrderInvoice(id))
    } catch (e) {
      message.error(e instanceof Error ? e.message : '下載 Invoice 失敗')
    } finally {
      setInvoiceDownloading(false)
    }
  }

  const openInvoiceModal = () => {
    invoiceForm.resetFields()
    setInvoiceModalOpen(true)
  }

  const issueInvoice = async (values: IssueOrderInvoiceRequest) => {
    if (!id) return
    setInvoiceDownloading(true)
    try {
      const blob = await issueOrderInvoice(id, {
        recipientName: values.recipientName.trim(),
        recipientAddress: values.recipientAddress.trim(),
        recipientEmail: values.recipientEmail?.trim() || undefined,
      })
      saveInvoicePdf(blob)
      const refreshed = await getOrder(id)
      setOrder(refreshed.data)
      setInvoiceModalOpen(false)
      message.success('Invoice 已簽發')
    } catch (e) {
      message.error(e instanceof Error ? e.message : '簽發 Invoice 失敗')
    } finally {
      setInvoiceDownloading(false)
    }
  }

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
  }

  if (!order) {
    return <div>訂單不存在</div>
  }

  const displayStatus = orderDisplayStatus(order.status, order.refundStatus)
  const showFees = shouldShowOrderFees(order.status)

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 24,
        }}
      >
        <Space size={24} wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/dashboard/orders')}>
            返回訂單列表
          </Button>
          <Title level={4} style={{ margin: 0 }}>訂單詳情</Title>
        </Space>
        <Space wrap>
          {canManageInvoice && (
            <Button
              icon={order.invoiceIssued ? <DownloadOutlined /> : <FilePdfOutlined />}
              loading={invoiceDownloading}
              onClick={order.invoiceIssued ? downloadExistingInvoice : openInvoiceModal}
            >
              {order.invoiceIssued ? '下載 Invoice' : '簽發並下載 Invoice'}
            </Button>
          )}
          {canRequestRefund && (
            <Button icon={<RollbackOutlined />} onClick={openRefundModal}>
              申請退款
            </Button>
          )}
        </Space>
      </div>
      <Card>
        <Descriptions bordered column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="訂單號">{order.id}</Descriptions.Item>
          <Descriptions.Item label="狀態">
            <Tag color={orderStatusColors[displayStatus]}>{orderStatusLabels[displayStatus] || displayStatus}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="客戶郵箱">{order.customerEmail || '-'}</Descriptions.Item>
          <Descriptions.Item label="客戶名稱">{order.customerName || '-'}</Descriptions.Item>
          <Descriptions.Item label="訂單金額">{formatAmount(orderAmount ?? order.amount)} {order.currency}</Descriptions.Item>
          <Descriptions.Item label="應付金額">{formatAmount(payableAmount ?? order.amount)} {order.currency}</Descriptions.Item>
          <Descriptions.Item label="匯率">
            {formatDisplayExchangeRate(order.currency, order.exchangeRate)}
          </Descriptions.Item>
          <Descriptions.Item label="應付金額(USD)">
            {formatOptionalUsdAmount(order.grossAmountUsd)}
          </Descriptions.Item>
          {showFees && <Descriptions.Item label="平台費率/平台每筆手續費">
            {formatRateAndPerTransactionFee(order.platformFeeRate, order.perTransactionFee)}
          </Descriptions.Item>}
          {showFees && <Descriptions.Item label="平台費用(USD)">
            {order.platformFeeUsd ? `${formatAmount(order.platformFeeUsd)} USD` : `${formatAmount(order.platformFee)} ${order.currency}`}
          </Descriptions.Item>}
          {showFees && <Descriptions.Item label="商戶分擔費率/商戶分擔每筆手續費">
            {formatRateAndPerTransactionFee(order.merchantFeeRate, order.merchantPerTransactionFee)}
          </Descriptions.Item>}
          {showFees && <Descriptions.Item label="客戶分擔費率/客戶分擔每筆手續費">
            {formatRateAndPerTransactionFee(order.payerFeeRate, order.payerPerTransactionFee)}
          </Descriptions.Item>}
          {showFees && <Descriptions.Item label="商戶分擔平台手續費(USD)">
            {formatOptionalUsdAmount(order.merchantFeeUsd)}
          </Descriptions.Item>}
          {showFees && <Descriptions.Item label="客戶分擔平台手續費(USD)">
            {formatOptionalUsdAmount(order.payerFeeUsd)}
          </Descriptions.Item>}
          <Descriptions.Item label="循環保證金">
            {formatOptionalAmount(order.rollingReserveAmount, order.currency)}
          </Descriptions.Item>
          <Descriptions.Item label="保證金釋放時間">
            {formatDate(order.rollingReserveReleaseAt)}
          </Descriptions.Item>
          <Descriptions.Item label="實際結算金額">
            {order.settledAmount ? `${formatAmount(order.settledAmount)} USD` : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="支付方式">{paymentMethodLabels[order.paymentMethod] || order.paymentMethod}</Descriptions.Item>
          <Descriptions.Item label="產品 ID">{order.productId}</Descriptions.Item>
          {order.productSnapshot && (
            <>
              <Descriptions.Item label="產品名稱（快照）">{order.productSnapshot.name}</Descriptions.Item>
              <Descriptions.Item label="產品價格（快照）">
                {order.productSnapshot.price} {order.productSnapshot.currency}
              </Descriptions.Item>
            </>
          )}
          <Descriptions.Item label="創建時間">{formatTimestamp(order.createdAt)}</Descriptions.Item>
          <Descriptions.Item label="支付時間">{formatTimestamp(order.paidAt)}</Descriptions.Item>
        </Descriptions>
      </Card>

      {order.subscription && (
        <Card title="訂閱資訊" style={{ marginTop: 16 }}>
          <Descriptions bordered column={2}>
            <Descriptions.Item label="訂閱 ID">
              <Button
                type="link"
                style={{ padding: 0 }}
                onClick={() => navigate(`/dashboard/subscriptions/${order.subscription!.subscriptionId}`)}
              >
                {order.subscription.subscriptionId}
              </Button>
            </Descriptions.Item>
            <Descriptions.Item label="賬單 ID">{order.subscription.subscriptionInvoiceId}</Descriptions.Item>
            <Descriptions.Item label="週期號">#{order.subscription.cycleNo}</Descriptions.Item>
            <Descriptions.Item label="計費原因">
              {billingReasonText[order.subscription.billingReason] || order.subscription.billingReason}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      <Card title="退款申請" style={{ marginTop: 16 }}>
        <Table
          rowKey="id"
          size="small"
          dataSource={refundRequests}
          pagination={false}
          locale={{ emptyText: '暫無退款申請' }}
          columns={[
            { title: '申請號', dataIndex: 'id' },
            { title: '金額', render: (_, r) => `${formatAmount(r.amount)} ${r.currency}` },
            { title: '狀態', dataIndex: 'status', render: (v) => {
              const meta = refundRequestStatusMeta[v] || { color: 'default', label: v }
              return <Tag color={meta.color}>{meta.label}</Tag>
            } },
            { title: '原因', dataIndex: 'reason' },
            { title: '審核備註', dataIndex: 'adminNote', render: (v) => v || '-' },
            { title: '提交時間', dataIndex: 'createdAt', render: (v) => formatTimestamp(v) },
          ]}
        />
      </Card>

      <Card title="退款記錄" style={{ marginTop: 16 }}>
        <Table
          rowKey="id"
          size="small"
          dataSource={refunds}
          pagination={false}
          locale={{ emptyText: '暫無退款記錄' }}
          columns={[
            { title: '退款號', dataIndex: 'id' },
            { title: '金額', render: (_, r) => `${formatAmount(r.amount)} ${r.currency}` },
            { title: '狀態', dataIndex: 'status', render: (v) => <Tag color={v === 'SUCCEEDED' ? 'green' : v === 'FAILED' ? 'red' : 'gold'}>{v}</Tag> },
            { title: '原因', dataIndex: 'reason' },
            { title: '完成時間', dataIndex: 'succeededAt', render: (v) => formatTimestamp(v) },
          ]}
        />
      </Card>

      <Modal
        title="簽發 Invoice"
        open={invoiceModalOpen}
        onCancel={() => setInvoiceModalOpen(false)}
        onOk={() => invoiceForm.submit()}
        confirmLoading={invoiceDownloading}
        okText="簽發並下載"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={invoiceForm} layout="vertical" onFinish={issueInvoice} preserve={false}>
          <Form.Item
            label="被開票方名稱"
            name="recipientName"
            rules={[
              { required: true, whitespace: true, message: '請輸入被開票方名稱' },
              { max: 200, message: '被開票方名稱不能超過 200 個字符' },
            ]}
          >
            <Input autoComplete="organization" />
          </Form.Item>
          <Form.Item
            label="被開票方地址"
            name="recipientAddress"
            rules={[
              { required: true, whitespace: true, message: '請輸入被開票方地址' },
              { max: 500, message: '被開票方地址不能超過 500 個字符' },
            ]}
          >
            <TextArea rows={3} autoComplete="street-address" />
          </Form.Item>
          <Form.Item
            label="聯絡郵箱（選填）"
            name="recipientEmail"
            rules={[
              { type: 'email', message: '請輸入有效的郵箱地址' },
              { max: 254, message: '郵箱地址不能超過 254 個字符' },
            ]}
          >
            <Input autoComplete="email" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="申請退款"
        open={refundModalOpen}
        onCancel={() => setRefundModalOpen(false)}
        onOk={submitRefundRequest}
        confirmLoading={submittingRefund}
        okText="提交申請"
        cancelText="取消"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Typography.Text type="secondary">
            默認退款金額為用戶實際支付的應付金額。
          </Typography.Text>
          <Input
            value={refundAmount}
            onChange={(e) => setRefundAmount(e.target.value)}
            addonAfter={order.currency}
            placeholder="退款金額"
          />
          <div style={{ marginBottom: 24 }}>
            <TextArea
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              rows={4}
              maxLength={500}
              showCount
              placeholder="退款原因"
            />
          </div>
        </Space>
      </Modal>
    </div>
  )
}
